import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import {
  getDailyClosingSummary,
  saveDayClosing,
  checkDayClosing,
  updateTransactionAmount,
  exportDailyClosingToExcel,
  getDailyClosingOpeningBalance,
  getXReport
} from "../../components/Api/GmsDayClosing";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { Edit, Download, Printer } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin,
  hasEditOrHigherPermission,
  hasApprovePermissionWithAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";
import html2pdf from "html2pdf.js";

// Custom styles for Select components (from PayrollProcessingPage)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Helper to normalize ID values
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  // Prioritize authUser (latest login response) - if it exists, use its values even if null
  // Only fall back to other sources if authUser doesn't exist or doesn't have these properties
  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    // authUser exists - use its values (even if null) as source of truth from latest login
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    // Fallback to other sources only if authUser doesn't exist
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

export default function DayClosingPage() {
  const [summary, setSummary] = useState(null); // Complete totals (always all transaction types)
  const [openingBalance, setOpeningBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("1"); // Default to Cash tab
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editCashAmount, setEditCashAmount] = useState(0);
  const [editCashlessAmount, setEditCashlessAmount] = useState(0);
  const [editNetAmount, setEditNetAmount] = useState(0);
  const [editNotes, setEditNotes] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [dayClosingNotes, setDayClosingNotes] = useState("");
  const [holidayNotes, setHolidayNotes] = useState("");
  const [showDayClosingModal, setShowDayClosingModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsDailyClosing');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsDailyClosing');
  const hasApprovePermission = hasApprovePermissionWithAdmin(permissions, '/GmsDailyClosing');

  // Get user session
  const session = useMemo(() => getUserSession(), []);
  const userBusinessId = session.businessId;
  const userBranchId = session.branchId;

  // Using centralized date helper from helpers/dateHelper.js

  // Load businesses
  const loadBusinesses = useCallback(async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);

      // If user has business, auto-select it
      if (userBusinessId && !selectedBusiness) {
        const userBusiness = businessData.find(b => b.business_Id === userBusinessId);
        if (userBusiness) {
          setSelectedBusiness({
            value: userBusinessId,
            label: userBusiness.business_NameEn || userBusiness.business_Name
          });
        }
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load businesses", "error");
    }
  }, [userBusinessId, selectedBusiness]);

  // Load branches based on selected business
  const loadBranches = useCallback(async (businessId) => {
    if (!businessId) {
      setBranches([]);
      return;
    }
    try {
      const response = await getBranches({ Business_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      setBranches(branchData);

      // If user has branch, auto-select it
      if (userBranchId && !selectedBranch) {
        const userBranch = branchData.find(b => b.branch_Id === userBranchId);
        if (userBranch) {
          setSelectedBranch({
            value: userBranchId,
            label: `${userBranch.branch_NameEn} - ${userBranch.branch_NameAr}`
          });
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  }, [userBranchId, selectedBranch]);

  // Get transaction type from active tab
  const getTransactionType = useCallback(() => {
    const type = (() => {
      switch (activeTab) {
        case "1": return "CASH";
        case "2": return "CASHLESS";
        case "3": return "CREDIT";
        case "4": return "RETURN";
        case "5": return "TABEE";
        case "6": return null; // All
        default: return "CASH";
      }
    })();
    console.log("🔍 [DayClosing] getTransactionType - activeTab:", activeTab, "-> transactionType:", type);
    return type;
  }, [activeTab]);

  // Load opening balance
  const loadOpeningBalance = useCallback(async () => {
    if (!selectedBusiness || !selectedBranch) {
      setOpeningBalance({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        tabeeTotal: 0,
        pettyCashReceiptsTotal: 0,
        pettyCashPaymentsTotal: 0,
        netTotal: 0,
        closingDate: null,
        isPreviousDayHoliday: false,
        hasNoClosing: false,
        hasGapsInClosing: false,
        previousDate: null
      });
      return;
    }

    try {
      const formattedDate = formatDateForAPI(selectedDate);
      const filter = {
        closingDate: formattedDate,
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      };

      const response = await getDailyClosingOpeningBalance(filter);

      setOpeningBalance(response || {
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        tabeeTotal: 0,
        pettyCashReceiptsTotal: 0,
        pettyCashPaymentsTotal: 0,
        netTotal: 0,
        closingDate: null,
        isPreviousDayHoliday: false,
        hasNoClosing: true,
        previousDate: null
      });
    } catch (error) {
      console.error("Error loading opening balance:", error);
      setOpeningBalance({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        tabeeTotal: 0,
        pettyCashReceiptsTotal: 0,
        pettyCashPaymentsTotal: 0,
        netTotal: 0,
        closingDate: null,
        isPreviousDayHoliday: false,
        hasNoClosing: true,
        previousDate: null
      });
    }
  }, [selectedDate, selectedBusiness, selectedBranch]);

  // Load complete summary (all transaction types) for totals display
  const loadCompleteSummary = useCallback(async () => {
    if (!selectedBusiness || !selectedBranch) {
      setSummary({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        tabeeTotal: 0,
        pettyCashReceiptsTotal: 0,
        pettyCashPaymentsTotal: 0,
        netTotal: 0
      });
      return;
    }

    const formattedDate = formatDateForAPI(selectedDate);

    try {
      const filter = {
        closingDate: formattedDate,
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        transactionType: null, // Always get complete totals
        page: 1,
        pageSize: 1 // We only need totals, not transactions
      };

      const response = await getDailyClosingSummary(filter);

      if (response) {
        setSummary({
          cashTotal: response.cashTotal || 0,
          cashlessTotal: response.cashlessTotal || 0,
          creditTotal: response.creditTotal || 0,
          returnsTotal: response.returnsTotal || 0,
          tabeeTotal: response.tabeeTotal || 0,
          pettyCashReceiptsTotal: response.pettyCashReceiptsTotal || 0,
          pettyCashPaymentsTotal: response.pettyCashPaymentsTotal || 0,
          netTotal: response.netTotal || 0
        });
      } else {
        setSummary({
          cashTotal: 0,
          cashlessTotal: 0,
          creditTotal: 0,
          returnsTotal: 0,
          tabeeTotal: 0,
          pettyCashReceiptsTotal: 0,
          pettyCashPaymentsTotal: 0,
          netTotal: 0
        });
      }
    } catch (error) {
      console.error("Error loading complete summary:", error);
      setSummary({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        tabeeTotal: 0,
        pettyCashReceiptsTotal: 0,
        pettyCashPaymentsTotal: 0,
        netTotal: 0
      });
    }
  }, [selectedDate, selectedBusiness, selectedBranch]);

  // Load daily closing transactions (filtered by tab)
  const loadDailyClosingData = useCallback(async () => {
    if (!selectedBusiness || !selectedBranch) {
      setTransactions([]);
      setTotalCount(0);
      return;
    }

    const transactionType = getTransactionType();
    const formattedDate = formatDateForAPI(selectedDate);

    setLoading(true);
    try {
      const filter = {
        closingDate: formattedDate,
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        transactionType: transactionType,
        page: page,
        pageSize: pageSize
      };

      const response = await getDailyClosingSummary(filter);

      if (response) {
        const transactionsList = response.transactions || [];
        setTransactions(transactionsList);
        setTotalCount(transactionsList.length);
      } else {
        setTransactions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error loading daily closing data:", error);
      Swal.fire("Error", "Failed to load daily closing data: " + (error.response?.data?.message || error.message), "error");
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedBusiness, selectedBranch, page, pageSize, activeTab, getTransactionType]);

  // Initial load
  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, loadBusinesses]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness, loadBranches]);

  // Load data when filters change (only when branch is selected)
  useEffect(() => {
    if (selectedBusiness && selectedBranch && hasPermission) {
      loadOpeningBalance();
      loadCompleteSummary();
      loadDailyClosingData();
    } else {
      if (!selectedBranch) {
        setSummary({
          cashTotal: 0,
          cashlessTotal: 0,
          creditTotal: 0,
          returnsTotal: 0,
          netTotal: 0
        });
        setOpeningBalance({
          cashTotal: 0,
          cashlessTotal: 0,
          creditTotal: 0,
          returnsTotal: 0,
          netTotal: 0,
          closingDate: null,
          isPreviousDayHoliday: false,
          hasNoClosing: false,
          previousDate: null
        });
        setTransactions([]);
        setTotalCount(0);
      }
    }
  }, [selectedDate, selectedBusiness, selectedBranch, page, pageSize, activeTab, loadDailyClosingData, loadCompleteSummary, loadOpeningBalance, hasPermission]);

  // Business options
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name
    }));
  }, [businesses]);

  // Branch options
  const branchOptions = useMemo(() => {
    return branches.map(b => ({
      value: b.branch_Id,
      label: `${b.branch_NameEn} - ${b.branch_NameAr}`
    }));
  }, [branches]);

  // Get paginated transactions
  const getPaginatedTransactions = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return transactions.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle edit transaction
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setEditCashAmount(transaction.cashAmount || 0);
    setEditCashlessAmount(transaction.cashlessAmount || 0);
    setEditNetAmount(transaction.netAmount || 0);
    setEditNotes("");
    setShowEditModal(true);
  };

  // Handle save edited transaction
  const handleSaveEdit = async () => {
    // Validate: Cash + Cashless must equal Net
    if (editCashAmount + editCashlessAmount !== editNetAmount) {
      Swal.fire("Error", `Cash amount + Bank amount must equal Net amount (${editNetAmount.toFixed(2)})`, "error");
      return;
    }

    setEditLoading(true);
    try {
      const request = {
        transactionId: editingTransaction.transactionId,
        tableType: editingTransaction.tableType,
        cashAmount: editCashAmount,
        cashlessAmount: editCashlessAmount,
        notes: editNotes || null
      };

      const response = await updateTransactionAmount(request);

      if (response.message) {
        Swal.fire("Success", response.message, "success");
        setShowEditModal(false);
        loadDailyClosingData(); // Reload data
      } else {
        Swal.fire("Error", "Failed to update transaction amount", "error");
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to update transaction amount", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Handle mark day closing
  const handleMarkDayClosing = () => {
    // Validate branch is selected
    if (!selectedBranch) {
      Swal.fire("Error", "Please select a branch before marking day closing.", "error");
      return;
    }

    // Validate date is selected
    if (!selectedDate) {
      Swal.fire("Error", "Please select a date before marking day closing.", "error");
      return;
    }

    // Reset notes and open modal
    setDayClosingNotes("");
    setShowDayClosingModal(true);
  };

  // Validate and save day closing
  const handleSaveDayClosing = async () => {
    // Validate date
    if (!selectedDate) {
      Swal.fire("Error", "Please select a date before marking day closing.", "error");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedClosingDate = new Date(selectedDate);
    selectedClosingDate.setHours(0, 0, 0, 0);

    // Check if future date
    if (selectedClosingDate > today) {
      Swal.fire("Error", "Cannot select future dates. Please select today or a previous date.", "error");
      return;
    }

    // Check if date already exists
    try {
      const checkRequest = {
        transactionDate: formatDateForAPI(selectedDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);

      if (dayClosingCheck.isClosed) {
        Swal.fire("Error", "Day closing or holiday already exists for this date. Please select a different date.", "error");
        return;
      }
    } catch (error) {
      console.error("Error checking day closing:", error);
      // Continue with save if check fails
    }

    // Show confirmation dialog
    const confirmResult = await Swal.fire({
      title: 'Confirm Day Closing',
      text: `Are you sure you want to mark day closing for ${formatDateForDisplay(selectedDate, 'date')}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#556ee6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Mark Day Closing',
      cancelButtonText: 'Cancel'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setLoading(true);

    try {
      const request = {
        closingDate: formatDateForAPI(selectedDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        holiday: false,
        notes: dayClosingNotes || null
      };

      const response = await saveDayClosing(request);

      if (response.message) {
        Swal.fire("Success", response.message, "success");
        setShowDayClosingModal(false);
        setDayClosingNotes("");
        loadDailyClosingData(); // Reload data
      } else {
        Swal.fire("Error", "Failed to mark day closing", "error");
      }
    } catch (error) {
      console.error("Error marking day closing:", error);
      const errorMessage = error.response?.data?.message || "Failed to mark day closing";
      if (errorMessage.includes("already") || errorMessage.includes("duplicate") || errorMessage.includes("unique")) {
        Swal.fire("Error", "Day closing or holiday already exists for this date. Please select a different date.", "error");
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle mark holiday
  const handleMarkHoliday = () => {
    // Validate branch is selected
    if (!selectedBranch) {
      Swal.fire("Error", "Please select a branch before marking as holiday.", "error");
      return;
    }

    // Validate date is selected
    if (!selectedDate) {
      Swal.fire("Error", "Please select a date before marking as holiday.", "error");
      return;
    }

    // Reset notes and open modal
    setHolidayNotes("");
    setShowHolidayModal(true);
  };

  // Validate and save holiday
  const handleSaveHoliday = async () => {
    // Validate date
    if (!selectedDate) {
      Swal.fire("Error", "Please select a date before marking as holiday.", "error");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedClosingDate = new Date(selectedDate);
    selectedClosingDate.setHours(0, 0, 0, 0);

    // Check if future date
    if (selectedClosingDate > today) {
      Swal.fire("Error", "Cannot select future dates. Please select today or a previous date.", "error");
      return;
    }

    // Check if date already exists
    try {
      const checkRequest = {
        transactionDate: formatDateForAPI(selectedDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);

      if (dayClosingCheck.isClosed) {
        Swal.fire("Error", "Day closing or holiday already exists for this date. Please select a different date.", "error");
        return;
      }
    } catch (error) {
      console.error("Error checking day closing:", error);
      // Continue with save if check fails
    }

    // Show confirmation dialog
    const confirmResult = await Swal.fire({
      title: 'Confirm Holiday',
      text: `Are you sure you want to mark ${formatDateForDisplay(selectedDate, 'date')} as a holiday?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ffc107',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Mark as Holiday',
      cancelButtonText: 'Cancel'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setLoading(true);

    try {
      const request = {
        closingDate: formatDateForAPI(selectedDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        holiday: true,
        notes: holidayNotes || null
      };

      const response = await saveDayClosing(request);

      if (response.message) {
        Swal.fire("Success", response.message, "success");
        setShowHolidayModal(false);
        setHolidayNotes("");
        loadDailyClosingData(); // Reload data
      } else {
        Swal.fire("Error", "Failed to mark holiday", "error");
      }
    } catch (error) {
      console.error("Error marking holiday:", error);
      const errorMessage = error.response?.data?.message || "Failed to mark holiday";
      if (errorMessage.includes("already") || errorMessage.includes("duplicate") || errorMessage.includes("unique")) {
        Swal.fire("Error", "Day closing or holiday already exists for this date. Please select a different date.", "error");
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle export to Excel (tab-specific)
  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const transactionType = getTransactionType();
      const filter = {
        closingDate: formatDateForAPI(selectedDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch?.value || null,
        transactionType: transactionType
      };

      await exportDailyClosingToExcel(filter);
      Swal.fire("Success", "Daily closing summary exported successfully", "success");
    } catch (error) {
      console.error("Error exporting:", error);
      Swal.fire("Error", "Failed to export daily closing summary", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle Print X Report
  const [xReportData, setXReportData] = useState(null);
  const [showXReportModal, setShowXReportModal] = useState(false);
  const xReportPrintRef = useRef(null);

  const handlePrintXReport = async () => {
    if (!selectedBusiness || !selectedBranch) {
      Swal.fire("Error", "Please select company and branch", "error");
      return;
    }

    try {
      setLoading(true);

      // Get business and branch names from selected options
      const businessName = selectedBusiness.label || businesses.find(b => b.business_Id === selectedBusiness.value)?.business_NameEn || "";
      const branchName = selectedBranch.label || branches.find(b => b.branch_Id === selectedBranch.value)?.branch_NameEn || "";
      const businessTRN = businesses.find(b => b.business_Id === selectedBusiness.value)?.business_TRN || null;

      const filter = {
        reportDate: formatDateForAPI(selectedDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        businessName: businessName,
        branchName: branchName,
        businessTRN: businessTRN
      };

      const reportData = await getXReport(filter);
      setXReportData(reportData);
      setShowXReportModal(true);
    } catch (error) {
      console.error("Error fetching X Report:", error);
      Swal.fire("Error", "Failed to fetch X Report data: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintXReportPDF = () => {
    if (!xReportPrintRef.current) return;

    const now = new Date();
    const formattedDate = now.toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
    const dateStr = formatDateForAPI(selectedDate).replace(/-/g, "");
    const fileName = `X_Report_${dateStr}_${formattedDate}`;

    const element = xReportPrintRef.current;

    const options = {
      margin: [10, 10, 10, 10],
      filename: `${fileName}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
      },
      jsPDF: {
        unit: "pt",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf().set(options).from(element).save();
  };

  // Calculate closing balance (Opening + Current)
  const getClosingBalance = useMemo(() => {
    if (!openingBalance || !summary) {
      return {
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        tabeeTotal: 0,
        pettyCashReceiptsTotal: 0,
        pettyCashPaymentsTotal: 0,
        netTotal: 0
      };
    }
    return {
      cashTotal: (openingBalance.cashTotal || 0) + (summary.cashTotal || 0),
      cashlessTotal: (openingBalance.cashlessTotal || 0) + (summary.cashlessTotal || 0),
      creditTotal: (openingBalance.creditTotal || 0) + (summary.creditTotal || 0),
      returnsTotal: (openingBalance.returnsTotal || 0) + (summary.returnsTotal || 0),
      tabeeTotal: (openingBalance.tabeeTotal || 0) + (summary.tabeeTotal || 0),
      pettyCashReceiptsTotal: (openingBalance.pettyCashReceiptsTotal || 0) + (summary.pettyCashReceiptsTotal || 0),
      pettyCashPaymentsTotal: (openingBalance.pettyCashPaymentsTotal || 0) + (summary.pettyCashPaymentsTotal || 0),
      netTotal: (openingBalance.netTotal || 0) + (summary.netTotal || 0)
    };
  }, [openingBalance, summary]);

  // Check if has transactions (for conditional button display)
  const hasTransactions = useMemo(() => {
    if (!summary) return false;
    return (
      (summary.cashTotal !== 0 && summary.cashTotal !== null) ||
      (summary.cashlessTotal !== 0 && summary.cashlessTotal !== null) ||
      (summary.creditTotal !== 0 && summary.creditTotal !== null) ||
      (summary.returnsTotal !== 0 && summary.returnsTotal !== null) ||
      (transactions && transactions.length > 0)
    );
  }, [summary, transactions]);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  // Format time
  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return "N/A";
    }
  };

  // Get transaction type label
  const getTransactionTypeLabel = (tableType) => {
    switch (tableType) {
      case 'SAL': return 'Sales Invoice';
      case 'SRT': return 'Sales Return';
      case 'REC': return 'Receipt';
      case 'ADV': return 'Advance Receipt';
      case 'CDP': return 'Cash Deposit';
      default: return tableType;
    }
  };

  if (!hasPermission) {
    return null;
  }

  const paginatedTransactions = getPaginatedTransactions();

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="X Report" breadcrumbItem="POS & Billing" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="card-title mb-3">X Report</h4>

                {/* Filters */}
                <Card className="mb-4">
                  <CardBody>
                    <Row>
                      <Col md="3">
                        <Label>Company</Label>
                        <Select
                          options={businessOptions}
                          value={selectedBusiness}
                          onChange={(option) => {
                            setSelectedBusiness(option);
                            setSelectedBranch(null);
                            setPage(1);
                          }}
                          isClearable={!userBusinessId}
                          isDisabled={userBusinessId !== null && userBusinessId !== undefined}
                          isSearchable
                          placeholder="Select Company"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="3">
                        <Label>Branch</Label>
                        <Select
                          options={branchOptions}
                          value={selectedBranch}
                          onChange={(option) => {
                            setSelectedBranch(option);
                            setPage(1);
                          }}
                          isClearable={!userBranchId}
                          isDisabled={!selectedBusiness || (userBranchId !== null && userBranchId !== undefined)}
                          isSearchable
                          placeholder="Select Branch"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="3">
                        <Label>Date</Label>
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => {
                            setSelectedDate(date);
                            setPage(1);
                          }}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          maxDate={new Date()}
                        />
                      </Col>
                      <Col md="3" className="d-flex align-items-end">
                        {/* Only users with Approve permission can mark day closing or holiday */}
                        {hasApprovePermission && (
                          <>
                            {hasTransactions ? (
                              <Button
                                color="primary"
                                size="sm"
                                onClick={handleMarkDayClosing}
                                disabled={loading || !selectedBusiness || !selectedBranch}
                                style={{ height: '38px' }}
                              >
                                Mark Day Closing
                              </Button>
                            ) : (
                              <Button
                                color="warning"
                                size="sm"
                                onClick={handleMarkHoliday}
                                disabled={loading || !selectedBusiness || !selectedBranch}
                                style={{ height: '38px' }}
                              >
                                Mark as Holiday
                              </Button>
                            )}
                          </>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Show message if business or branch not selected */}
                {(!selectedBusiness || !selectedBranch) && (
                  <Card className="mb-4" style={{ border: '2px solid #ffc107' }}>
                    <CardBody className="text-center py-4">
                      <i className="mdi mdi-information-outline" style={{ fontSize: '48px', color: '#ffc107', marginBottom: '15px' }}></i>
                      <h5 style={{ color: '#856404', marginBottom: '10px' }}>Please Select Company and Branch</h5>
                      <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {!selectedBusiness && !selectedBranch
                          ? "Please select a Company and Branch from the dropdowns above to view the X Report."
                          : !selectedBusiness
                            ? "Please select a Company from the dropdown above to view the X Report."
                            : "Please select a Branch from the dropdown above to view the X Report."}
                      </p>
                    </CardBody>
                  </Card>
                )}

                {/* Opening Balance - Above Tabs */}
                {selectedBusiness && selectedBranch && openingBalance && (
                  <Card className="mb-3" style={{ border: '2px solid #28a745' }}>
                    <CardBody style={{ padding: '0.5rem' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <h6 className="mb-0" style={{ color: '#28a745', fontWeight: 'bold', fontSize: '13px' }}>
                          Opening Balance (Up to Yesterday)
                        </h6>
                        <div className="d-flex gap-2 align-items-center">
                          {openingBalance.isPreviousDayHoliday && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: '11px' }}>
                              <i className="mdi mdi-information-outline me-1"></i>
                              Previous day was a holiday
                            </span>
                          )}
                          {openingBalance.hasNoClosing && !openingBalance.isPreviousDayHoliday && !openingBalance.hasGapsInClosing && (
                            <span className="badge bg-info text-white" style={{ fontSize: '11px' }}>
                              <i className="mdi mdi-information-outline me-1"></i>
                              No day closing done yet
                            </span>
                          )}
                          {openingBalance.hasGapsInClosing && !openingBalance.isPreviousDayHoliday && (
                            <span className="badge bg-danger text-white" style={{ fontSize: '11px' }}>
                              <i className="mdi mdi-alert-circle me-1"></i>
                              Previous day closing not done
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: '0.5rem' }}>
                        <div className="d-flex" style={{ gap: '0.5rem', flex: '1' }}>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Cash Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.cashTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Bank Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.cashlessTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Credit Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.creditTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Returns Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.returnsTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Tabee Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.tabeeTotal || 0)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>PC Receipts</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.pettyCashReceiptsTotal || 0)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>PC Payments</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.pettyCashPaymentsTotal || 0)}</h6>
                            </div>
                          </div>
                        </div>
                        <div style={{ flex: '0 0 auto', minWidth: '16.66%' }}>
                          <div className="text-center p-1" style={{ backgroundColor: '#28a745', borderRadius: '0.375rem', color: 'white' }}>
                            <Label className="mb-0" style={{ color: 'white', fontSize: '11px' }}>Net Total</Label>
                            <h6 className="mb-0" style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                              {formatCurrency(openingBalance.netTotal)}
                            </h6>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Tabs Navigation */}
                {selectedBusiness && selectedBranch && (
                  <Card className="mb-4">
                    <CardBody className="p-0">
                      <div className="d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #dee2e6' }}>
                        <Nav tabs className="nav-tabs-custom" style={{ flex: 1 }}>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: activeTab === "1" ? "bold" : "normal",
                                color: activeTab === "1" ? "#556ee6" : "#74788d",
                                borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                                padding: "15px 25px",
                                fontSize: "16px"
                              }}
                              className={activeTab === "1" ? "active" : ""}
                              onClick={() => {
                                setActiveTab("1");
                                setPage(1);
                              }}
                            >
                              Cash
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: activeTab === "2" ? "bold" : "normal",
                                color: activeTab === "2" ? "#556ee6" : "#74788d",
                                borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                                padding: "15px 25px",
                                fontSize: "16px"
                              }}
                              className={activeTab === "2" ? "active" : ""}
                              onClick={() => {
                                setActiveTab("2");
                                setPage(1);
                              }}
                            >
                              Bank
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: activeTab === "3" ? "bold" : "normal",
                                color: activeTab === "3" ? "#556ee6" : "#74788d",
                                borderBottom: activeTab === "3" ? "2px solid #556ee6" : "none",
                                padding: "15px 25px",
                                fontSize: "16px"
                              }}
                              className={activeTab === "3" ? "active" : ""}
                              onClick={() => {
                                setActiveTab("3");
                                setPage(1);
                              }}
                            >
                              Credit
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: activeTab === "4" ? "bold" : "normal",
                                color: activeTab === "4" ? "#556ee6" : "#74788d",
                                borderBottom: activeTab === "4" ? "2px solid #556ee6" : "none",
                                padding: "15px 25px",
                                fontSize: "16px"
                              }}
                              className={activeTab === "4" ? "active" : ""}
                              onClick={() => {
                                setActiveTab("4");
                                setPage(1);
                              }}
                            >
                              Sales Return
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: activeTab === "5" ? "bold" : "normal",
                                color: activeTab === "5" ? "#556ee6" : "#74788d",
                                borderBottom: activeTab === "5" ? "2px solid #556ee6" : "none",
                                padding: "15px 25px",
                                fontSize: "16px"
                              }}
                              className={activeTab === "5" ? "active" : ""}
                              onClick={() => {
                                setActiveTab("5");
                                setPage(1);
                              }}
                            >
                              Tabby
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: activeTab === "6" ? "bold" : "normal",
                                color: activeTab === "6" ? "#556ee6" : "#74788d",
                                borderBottom: activeTab === "6" ? "2px solid #556ee6" : "none",
                                padding: "15px 25px",
                                fontSize: "16px"
                              }}
                              className={activeTab === "6" ? "active" : ""}
                              onClick={() => {
                                setActiveTab("6");
                                setPage(1);
                              }}
                            >
                              All
                            </NavLink>
                          </NavItem>
                        </Nav>
                        <div className="d-flex align-items-center gap-2" style={{ paddingRight: '15px' }}>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handlePrintXReport}
                            disabled={loading || !selectedBusiness || !selectedBranch}
                            style={{ height: '38px', fontSize: '12px', padding: '0 12px' }}
                          >
                            <Printer className="me-1" size={14} />
                            Print X Report
                          </Button>
                          <Button
                            color="success"
                            size="sm"
                            onClick={handleExportToExcel}
                            disabled={loading || !selectedBusiness || !selectedBranch}
                            style={{ height: '38px', fontSize: '12px', padding: '0 12px' }}
                          >
                            <Download className="me-1" size={14} />
                            Export to Excel
                          </Button>
                        </div>
                      </div>

                      <TabContent activeTab={activeTab} className="p-4">

                        {/* Transactions Table */}
                        {selectedBusiness && selectedBranch && (
                          <TabPane tabId={activeTab}>
                            <Card>
                              <CardBody>
                                <h5 className="card-title mb-3">
                                  Transactions ({totalCount} records)
                                </h5>
                                <div className="table-responsive">
                                  {loading ? (
                                    <div className="text-center py-4">
                                      <ClipLoader color="#556ee6" loading={loading} size={35} />
                                      <p className="mt-2">Loading transactions...</p>
                                    </div>
                                  ) : (
                                    <>
                                      <Table bordered>
                                        <thead className="table-light">
                                          <tr>
                                            <th>Sr.No</th>
                                            <th>Transaction Number</th>
                                            <th>Transaction Type</th>
                                            {(activeTab === "1" || activeTab === "4" || activeTab === "6") && <th>Cash Amount</th>}
                                            {(activeTab === "2" || activeTab === "4" || activeTab === "6") && <th>Bank Amount</th>}
                                            {(activeTab === "3" || activeTab === "6") && <th>Credit Amount</th>}
                                            {(activeTab === "5" || activeTab === "6") && <th>Tabee Amount</th>}
                                            <th>Date</th>
                                            <th>Time</th>
                                            <th>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {paginatedTransactions.length === 0 ? (
                                            <tr>
                                              <td colSpan={
                                                3 + // Sr.No, Transaction Number, Transaction Type
                                                ((activeTab === "1" || activeTab === "4" || activeTab === "6") ? 1 : 0) + // Cash
                                                ((activeTab === "2" || activeTab === "4" || activeTab === "6") ? 1 : 0) + // Bank
                                                ((activeTab === "3" || activeTab === "6") ? 1 : 0) + // Credit
                                                ((activeTab === "5" || activeTab === "6") ? 1 : 0) + // Tabee
                                                2 + // Date, Time
                                                1 // Actions
                                              } className="text-center">
                                                No transactions found
                                              </td>
                                            </tr>
                                          ) : (
                                            paginatedTransactions.map((transaction, index) => (
                                              <tr key={transaction.transactionId}>
                                                <td>{(page - 1) * pageSize + index + 1}</td>
                                                <td>{transaction.transactionNumber}</td>
                                                <td>{getTransactionTypeLabel(transaction.tableType)}</td>
                                                {(activeTab === "1" || activeTab === "4" || activeTab === "6") && (
                                                  <td style={{ color: transaction.cashAmount < 0 ? '#dc3545' : 'inherit' }}>
                                                    {transaction.cashAmount < 0 ? '-' : ''}{formatCurrency(Math.abs(transaction.cashAmount))}
                                                  </td>
                                                )}
                                                {(activeTab === "2" || activeTab === "4" || activeTab === "6") && (
                                                  <td>{formatCurrency(transaction.cashlessAmount)}</td>
                                                )}
                                                {(activeTab === "3" || activeTab === "6") && (
                                                  <td>{formatCurrency(transaction.creditAmount)}</td>
                                                )}
                                                {(activeTab === "5" || activeTab === "6") && (
                                                  <td>{formatCurrency(transaction.tabeeAmount || 0)}</td>
                                                )}
                                                <td>{formatDate(transaction.transactionDate)}</td>
                                                <td>{formatTime(transaction.transactionDateTime)}</td>
                                                <td>
                                                  {/* Only show edit button for sales invoices (SAL) that are NOT credit transactions */}
                                                  {/* Only users with Edit permission or higher can edit */}
                                                  {transaction.tableType === 'SAL' &&
                                                    transaction.creditAmount === 0 &&
                                                    (transaction.cashAmount > 0 || transaction.cashlessAmount > 0) &&
                                                    hasEditOrHigher ? (
                                                    <Button
                                                      color="info"
                                                      size="sm"
                                                      onClick={() => handleEditTransaction(transaction)}
                                                    >
                                                      <Edit size={14} className="me-1" />
                                                      Edit
                                                    </Button>
                                                  ) : (
                                                    <span className="text-muted" style={{ fontSize: '12px' }}>-</span>
                                                  )}
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </Table>

                                      {/* Pagination */}
                                      {totalCount > pageSize && (
                                        <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                                          style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                                          <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                                            <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                                            Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Button
                                              color="light"
                                              size="sm"
                                              disabled={page === 1}
                                              onClick={() => handlePageChange(page - 1)}
                                              style={{
                                                border: '1px solid #dee2e6',
                                                borderRadius: '0.375rem',
                                                minWidth: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                                color: page === 1 ? '#6c757d' : '#495057'
                                              }}
                                            >
                                              <i className="mdi mdi-chevron-left"></i>
                                            </Button>

                                            {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                              const pageNum = index + 1;
                                              const isActive = page === pageNum;

                                              return (
                                                <Button
                                                  key={pageNum}
                                                  color={isActive ? "primary" : "light"}
                                                  size="sm"
                                                  onClick={() => handlePageChange(pageNum)}
                                                  style={{
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '0.375rem',
                                                    minWidth: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: isActive ? '#556ee6' : '#fff',
                                                    borderColor: isActive ? '#556ee6' : '#dee2e6',
                                                    color: isActive ? '#fff' : '#495057',
                                                    fontWeight: isActive ? '600' : '400',
                                                    boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                                  }}
                                                >
                                                  {pageNum}
                                                </Button>
                                              );
                                            })}

                                            <Button
                                              color="light"
                                              size="sm"
                                              disabled={page === Math.ceil(totalCount / pageSize)}
                                              onClick={() => handlePageChange(page + 1)}
                                              style={{
                                                border: '1px solid #dee2e6',
                                                borderRadius: '0.375rem',
                                                minWidth: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                                color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                              }}
                                            >
                                              <i className="mdi mdi-chevron-right"></i>
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          </TabPane>
                        )}
                      </TabContent>
                    </CardBody>
                  </Card>
                )}

                {/* Current Day Totals */}
                {selectedBusiness && selectedBranch && summary && (
                  <Card className="mb-3" style={{ border: '2px solid #556ee6' }}>
                    <CardBody style={{ padding: '0.5rem' }}>
                      <h6 className="mb-1" style={{ color: '#556ee6', fontWeight: 'bold', fontSize: '13px' }}>
                        Current Day Totals ({formatDateForDisplay(selectedDate, 'date')})
                      </h6>
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: '0.5rem' }}>
                        <div className="d-flex" style={{ gap: '0.5rem', flex: '1' }}>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Cash Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.cashTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Bank Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.cashlessTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Credit Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.creditTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Returns Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.returnsTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Tabee Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.tabeeTotal || 0)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>PC Receipts</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.pettyCashReceiptsTotal || 0)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>PC Payments</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.pettyCashPaymentsTotal || 0)}</h6>
                            </div>
                          </div>
                        </div>
                        <div style={{ flex: '0 0 auto', minWidth: '16.66%' }}>
                          <div className="text-center p-1" style={{ backgroundColor: '#556ee6', borderRadius: '0.375rem', color: 'white' }}>
                            <Label className="mb-0" style={{ color: 'white', fontSize: '11px' }}>Net Total</Label>
                            <h6 className="mb-0" style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                              {formatCurrency(summary.netTotal)}
                            </h6>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Closing Balance */}
                {selectedBusiness && selectedBranch && openingBalance && summary && (
                  <Card className="mb-3" style={{ border: '2px solid #dc3545' }}>
                    <CardBody style={{ padding: '0.5rem' }}>
                      <h6 className="mb-1" style={{ color: '#dc3545', fontWeight: 'bold', fontSize: '13px' }}>
                        Closing Balance (Opening + Current)
                      </h6>
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: '0.5rem' }}>
                        <div className="d-flex" style={{ gap: '0.5rem', flex: '1' }}>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Cash Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.cashTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Bank Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.cashlessTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Credit Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.creditTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Returns Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.returnsTotal)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Tabee Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.tabeeTotal || 0)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>PC Receipts</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.pettyCashReceiptsTotal || 0)}</h6>
                            </div>
                          </div>
                          <div style={{ flex: '1' }}>
                            <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>PC Payments</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.pettyCashPaymentsTotal || 0)}</h6>
                            </div>
                          </div>
                        </div>
                        <div style={{ flex: '0 0 auto', minWidth: '16.66%' }}>
                          <div className="text-center p-1" style={{ backgroundColor: '#dc3545', borderRadius: '0.375rem', color: 'white' }}>
                            <Label className="mb-0" style={{ color: 'white', fontSize: '11px' }}>Net Total</Label>
                            <h6 className="mb-0" style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                              {formatCurrency(getClosingBalance.netTotal)}
                            </h6>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Edit Transaction Modal */}
      <Modal isOpen={showEditModal} toggle={() => setShowEditModal(false)}>
        <ModalHeader toggle={() => setShowEditModal(false)}>
          Edit Transaction Amount
        </ModalHeader>
        <ModalBody>
          {editingTransaction && (
            <Form>
              <FormGroup>
                <Label>Transaction Number</Label>
                <Input value={editingTransaction.transactionNumber} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Net Amount</Label>
                <Input value={formatCurrency(editNetAmount)} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Cash Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editCashAmount}
                  onChange={(e) => {
                    const cash = parseFloat(e.target.value) || 0;
                    setEditCashAmount(cash);
                    setEditCashlessAmount(editNetAmount - cash);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Bank Amount (Cashless) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editCashlessAmount}
                  onChange={(e) => {
                    const cashless = parseFloat(e.target.value) || 0;
                    setEditCashlessAmount(cashless);
                    setEditCashAmount(editNetAmount - cashless);
                  }}
                />
              </FormGroup>
              {editCashAmount + editCashlessAmount !== editNetAmount && (
                <div className="alert alert-warning">
                  Cash + Bank must equal Net Amount ({formatCurrency(editNetAmount)})
                </div>
              )}
              <FormGroup>
                <Label>Notes (Optional)</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Enter notes for this change..."
                />
              </FormGroup>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveEdit}
            disabled={editLoading || editCashAmount + editCashlessAmount !== editNetAmount}
          >
            {editLoading ? <ClipLoader size={20} color="#fff" /> : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Day Closing Modal */}
      <Modal isOpen={showDayClosingModal} toggle={() => {
        setShowDayClosingModal(false);
        setDayClosingNotes("");
      }}>
        <ModalHeader toggle={() => {
          setShowDayClosingModal(false);
          setDayClosingNotes("");
        }}>
          Mark Day Closing
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Selected Date</Label>
              <Input
                type="text"
                value={selectedDate ? formatDateForDisplay(selectedDate, 'date') : ''}
                disabled
                className="form-control"
                style={{ backgroundColor: '#f8f9fa' }}
              />
              <small className="text-muted">This will mark day closing for the date selected on the page.</small>
            </FormGroup>
            <FormGroup>
              <Label>Notes (Optional)</Label>
              <Input
                type="textarea"
                rows="3"
                value={dayClosingNotes}
                onChange={(e) => setDayClosingNotes(e.target.value)}
                placeholder="Enter notes for day closing..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowDayClosingModal(false);
            setDayClosingNotes("");
          }}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveDayClosing}
            disabled={loading || !selectedDate}
          >
            {loading ? "Saving..." : "Mark Day Closing"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Holiday Modal */}
      <Modal isOpen={showHolidayModal} toggle={() => {
        setShowHolidayModal(false);
        setHolidayNotes("");
      }}>
        <ModalHeader toggle={() => {
          setShowHolidayModal(false);
          setHolidayNotes("");
        }}>
          Mark as Holiday
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Selected Date</Label>
              <Input
                type="text"
                value={selectedDate ? formatDateForDisplay(selectedDate, 'date') : ''}
                disabled
                className="form-control"
                style={{ backgroundColor: '#f8f9fa' }}
              />
              <small className="text-muted">This will mark the date selected on the page as a holiday.</small>
            </FormGroup>
            <FormGroup>
              <Label>Notes (Optional)</Label>
              <Input
                type="textarea"
                rows="3"
                value={holidayNotes}
                onChange={(e) => setHolidayNotes(e.target.value)}
                placeholder="Enter notes for holiday..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowHolidayModal(false);
            setHolidayNotes("");
          }}>
            Cancel
          </Button>
          <Button
            color="warning"
            onClick={handleSaveHoliday}
            disabled={loading || !selectedDate}
          >
            {loading ? "Saving..." : "Mark as Holiday"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* X Report Print Modal */}
      <Modal isOpen={showXReportModal} toggle={() => setShowXReportModal(false)} size="lg">
        <ModalHeader toggle={() => setShowXReportModal(false)}>
          X Report - {xReportData && formatDateForDisplay(xReportData.reportDate, 'date')}
        </ModalHeader>
        <ModalBody>
          {xReportData && (
            <div ref={xReportPrintRef} className="border p-4">
              <div className="text-center mb-4">
                <h4><strong>{(xReportData.businessBranchInfo?.businessName || 'COMPANY').toUpperCase()}</strong></h4>
                {xReportData.businessBranchInfo?.branchName && <h5>{xReportData.businessBranchInfo.branchName}</h5>}
                <h4>X REPORT</h4>
                {xReportData.businessBranchInfo?.businessTRN && xReportData.businessBranchInfo.businessTRN.trim() !== '' && (
                  <p className="mb-0"><strong>TRN:</strong> {xReportData.businessBranchInfo.businessTRN}</p>
                )}
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <table className="table table-bordered table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Report Date:</strong> {formatDateForDisplay(xReportData.reportDate, 'date')}</td>
                        <td><strong>Report Time:</strong> {formatDateForDisplay(xReportData.reportDateTime, 'datetime')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary Totals */}
              <div className="row mb-3">
                <div className="col-12">
                  <h5 className="mb-2">Summary</h5>
                  <table className="table table-bordered table-sm">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Total Sales (Net Total)</strong></td>
                        <td className="text-end"><strong>{formatCurrency(xReportData.summary?.netTotal || 0)}</strong></td>
                      </tr>
                      <tr>
                        <td>Cash Total</td>
                        <td className="text-end">{formatCurrency(xReportData.summary?.cashTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Bank Total</td>
                        <td className="text-end">{formatCurrency(xReportData.summary?.cashlessTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Credit Total</td>
                        <td className="text-end">{formatCurrency(xReportData.summary?.creditTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Tabee Total</td>
                        <td className="text-end">{formatCurrency(xReportData.summary?.tabeeTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Petty Cash Receipts Total</td>
                        <td className="text-end">{formatCurrency(xReportData.summary?.pettyCashReceiptsTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Petty Cash Payments Total</td>
                        <td className="text-end">{formatCurrency(xReportData.summary?.pettyCashPaymentsTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Sales Return Total</td>
                        <td className="text-end">{formatCurrency(xReportData.summary?.returnsTotal || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tax and Discount */}
              {(xReportData.taxDiscountTotals?.totalTaxAmount > 0 || xReportData.taxDiscountTotals?.totalDiscountAmount > 0) && (
                <div className="row mb-3">
                  <div className="col-12">
                    <table className="table table-bordered table-sm">
                      <tbody>
                        {xReportData.taxDiscountTotals?.totalTaxAmount > 0 && (
                          <tr>
                            <td><strong>Total Tax</strong></td>
                            <td className="text-end"><strong>{formatCurrency(xReportData.taxDiscountTotals.totalTaxAmount)}</strong></td>
                          </tr>
                        )}
                        {xReportData.taxDiscountTotals?.totalDiscountAmount > 0 && (
                          <tr>
                            <td><strong>Total Discount</strong></td>
                            <td className="text-end"><strong>{formatCurrency(xReportData.taxDiscountTotals.totalDiscountAmount)}</strong></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Opening Cash */}
              {xReportData.openingBalance && (
                <div className="row mb-3">
                  <div className="col-12">
                    <h5 className="mb-2">Opening Cash</h5>
                    <table className="table table-bordered table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Date/Time:</strong> {xReportData.openingBalance.closingDate ? formatDateForDisplay(xReportData.openingBalance.closingDate, 'datetime') : 'N/A'}</td>
                        </tr>
                        <tr>
                          <td><strong>Opening Cash:</strong> {formatCurrency(xReportData.openingBalance.cashTotal || 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handlePrintXReportPDF}>
            <Printer size={16} className="me-2" />
            Print PDF
          </Button>
          <Button color="secondary" onClick={() => setShowXReportModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

